import { api } from '../../core/network/api';

/**
 * Thin REST wrappers for the player's own building/roster/tech state, used by
 * the notifications poller to detect timer completions. No game logic here —
 * just typed fetches.
 */

export interface BuildingStatus {
  type: string;
  level: number;
  constructing: boolean;
  constructionEndAt: string | null;
}

export interface RosterEntry {
  unitType: string;
  level: number;
  quantityReserved: number;
  training: boolean;
  trainEndAt: string | null;
  trainQuantity: number;
  locked: boolean;
}

export interface TechStatus {
  techLevel: number;
  researching: boolean;
  researchEndAt: string | null;
  unlockedCategories: string[];
}

export function fetchBuildings(): Promise<BuildingStatus[]> {
  return api.get<BuildingStatus[]>('/me/buildings');
}

export function fetchRoster(): Promise<RosterEntry[]> {
  return api.get<RosterEntry[]>('/me/roster');
}

export function fetchTech(): Promise<TechStatus> {
  return api.get<TechStatus>('/me/tech');
}

export type NotificationKind = 'ATTACK_INCOMING' | 'ATTACK_RESULT' | 'DEFENCE_RESULT' | 'MARCH_CANCELLED';

export interface AttackIncomingPayload {
  arrivalAt: string;
  targetColonyName: string;
}

export interface AttackResultPayload {
  attackerWins: boolean;
  defenderColonyName: string;
  plunder: Partial<Record<string, number>>;
  unitsLost: number;
  captured: boolean;
  /** A scouting mission: `attackerWins` then means intel was gained. */
  isRecon?: boolean;
}

export interface DefenceResultPayload {
  attackerWins: boolean;
  attackerColonyName: string;
  attackerUsername: string | null;
  defenderColonyName: string;
  plunderTaken: Partial<Record<string, number>>;
  unitsLost: number;
  colonyLost: boolean;
  isRecon?: boolean;
}

export interface MarchCancelledPayload {
  defenderColonyName: string;
  unitsReturned: number;
}

export interface GameNotification {
  id: string;
  kind: NotificationKind;
  colonyId: string | null;
  battleId?: string | null;
  payload: AttackIncomingPayload | AttackResultPayload | DefenceResultPayload | MarchCancelledPayload;
  createdAt: string;
  readAt: string | null;
}

export interface NotificationFeed {
  items: GameNotification[];
  unreadCount: number;
}

/** Also drives lazy battle resolution server-side — see BattleService.syncNotificationsFor. */
export function fetchNotifications(limit = 20): Promise<NotificationFeed> {
  return api.get<NotificationFeed>(`/me/notifications?limit=${limit}`);
}

/** `ids` omitted marks every unread row read. */
export function markNotificationsRead(ids?: string[]): Promise<{ unreadCount: number }> {
  return api.post<{ unreadCount: number }>('/me/notifications/read', ids ? { ids } : {});
}
